import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container, Typography, Box, CircularProgress, Alert,
  Table, TableHead, TableRow, TableCell, TableBody,
  Card, CardContent, CardActionArea, Grid, FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import * as api from '../../api';
import { getScoresWithPoints } from '../../utils/scoring';
import { toDivisionKey, toCategoryKey } from '../../components/ShooterForm';
import Header from '../../components/Header';

function fmtDate(d) {
  if (!d) return '';
  return `${d.year}-${String(d.month).padStart(2, '0')}-${String(d.day).padStart(2, '0')}`;
}

// Retry on transient failures (Lambda cold-start 503s, network errors).
async function withRetry(fn, attempts = 4) {
  for (let i = 0; ; i++) {
    try {
      return await fn();
    } catch (err) {
      const status = err.response?.status;
      const transient = !status || status === 502 || status === 503 || status === 504 || status === 429;
      if (i >= attempts - 1 || !transient) throw err;
      await new Promise((res) => setTimeout(res, 200 * 2 ** i));
    }
  }
}

export default function LeagueHome({ locale }) {
  const { leagueId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [league, setLeague] = useState(null);
  const [matches, setMatches] = useState(null); // [{ match, scores, shooters, stages }, ...]
  const [error, setError] = useState(null);
  const [division, setDivision] = useState('all');
  const [category, setCategory] = useState('all');

  useEffect(() => {
    let cancelled = false;
    setError(null);
    withRetry(() => api.getLeague(leagueId)).then(async (r) => {
      if (cancelled) return;
      const lg = r.data;
      setLeague(lg);
      // Each match is independent. Retry every sub-call so a transient 503 doesn't
      // silently drop a whole match column. If a match itself can't be fetched
      // after retries, render it as a stub so the column still appears with "—".
      const fetched = await Promise.all((lg.matches || []).map(async (matchId) => {
        try {
          const matchRes = await withRetry(() => api.getMatch(matchId));
          const match = matchRes.data;
          const [scoresRes, shootersRes] = await Promise.all([
            withRetry(() => api.getScores(matchId)).catch(() => ({ data: [] })),
            withRetry(() => api.getShooters(matchId)).catch(() => ({ data: [] })),
          ]);
          const stageIds = (match.stages || []).join(',');
          const stagesRes = stageIds
            ? await withRetry(() => api.getStages(stageIds)).catch(() => ({ data: [] }))
            : { data: [] };
          const stages = Array.isArray(stagesRes.data) ? stagesRes.data : (stagesRes.data ? [stagesRes.data] : []);
          const scores = Array.isArray(scoresRes.data) ? scoresRes.data : (scoresRes.data?.scores || []);
          return { match, scores, shooters: shootersRes.data || [], stages };
        } catch {
          // getMatch failed even after retries — keep the column with a stub
          return { match: { id: matchId, title: '', startDate: null, stages: [] }, scores: [], shooters: [], stages: [] };
        }
      }));
      if (!cancelled) setMatches(fetched);
    }).catch((e) => {
      if (!cancelled) setError(e.response?.data?.detail || e.message);
    });
    return () => { cancelled = true; };
  }, [leagueId]);

  const standings = useMemo(() => {
    if (!league || !matches) return null;
    const bestN = league.scoring?.type === 'best'
      ? (league.scoring.best || matches.length)
      : matches.length;

    const byShooter = {};
    matches.forEach(({ match, scores, shooters, stages }) => {
      if (!scores?.length || !shooters?.length) return;
      let scoresOverall = [];
      let dqs = [];
      try {
        ({ scoresOverall, dqs } = getScoresWithPoints({ scores, shooters, stages }));
      } catch { return; }
      scoresOverall.forEach((s) => {
        const pid = (s.shooterPublicId || '').split('-')[0];
        if (!pid) return;
        const key = `${pid}|${s.division}|${s.category || 'regular'}`;
        byShooter[key] = byShooter[key] || {
          publicId: pid,
          name: s.shooterName,
          division: s.division,
          category: s.category || 'regular',
          perMatch: {},
        };
        const pct = s.percentage != null ? s.percentage : 0;
        byShooter[key].perMatch[match.id] = pct;
      });
      dqs.forEach((d) => {
        const pid = (d.shooterPublicId || '').split('-')[0];
        if (!pid) return;
        const key = `${pid}|${d.division}|${d.category || 'regular'}`;
        byShooter[key] = byShooter[key] || {
          publicId: pid,
          name: d.shooterName,
          division: d.division,
          category: d.category || 'regular',
          perMatch: {},
        };
        byShooter[key].perMatch[match.id] = 'DQ';
      });
    });

    return Object.values(byShooter).map((entry) => {
      const scoresArr = (league.matches || [])
        .map((mid) => entry.perMatch[mid] ?? 0)
        .map((v) => (v === 'DQ' ? 0 : v))
        .sort((a, b) => b - a);
      const total = scoresArr.slice(0, bestN).reduce((sum, v) => sum + v, 0);
      return { ...entry, total };
    });
  }, [league, matches]);

  const divisions = useMemo(() => {
    if (!standings) return [];
    return Array.from(new Set(standings.map((s) => s.division))).filter(Boolean).sort();
  }, [standings]);

  const categories = useMemo(() => {
    if (!standings) return [];
    return Array.from(new Set(standings.map((s) => s.category))).filter(Boolean).sort();
  }, [standings]);

  const displayed = useMemo(() => {
    if (!standings) return [];
    return standings
      .filter((s) => (division === 'all' || s.division === division))
      .filter((s) => (category === 'all' || s.category === category))
      .sort((a, b) => b.total - a.total);
  }, [standings, division, category]);

  const sortedMatches = useMemo(() => {
    if (!matches) return [];
    const key = (m) => {
      const d = m?.startDate;
      if (!d) return Number.POSITIVE_INFINITY;
      return d.year * 10000 + d.month * 100 + d.day;
    };
    return [...matches].sort((a, b) => key(a.match) - key(b.match));
  }, [matches]);

  if (error) return <Container sx={{ mt: 4 }}><Alert severity="error">{error}</Alert></Container>;
  if (!league || !matches) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 8 }}>
        <CircularProgress />
        <Typography sx={{ mt: 1 }} color="text.secondary">{t('league.loadingResults', 'Loading league results…')}</Typography>
      </Box>
    );
  }

  return (
    <>
      <Header locale={locale} />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
        <Typography variant="h4" sx={{ mb: 1 }}>{league?.title || leagueId}</Typography>
        {league?.description && <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>{league.description}</Typography>}
        {league?.scoring?.type === 'best' && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('league.bestOf', { best: league.scoring.best, total: league.matches.length, defaultValue: 'Best {{best}} of {{total}} matches count for each shooter.' })}
          </Typography>
        )}

        <Typography variant="h6" sx={{ mb: 2 }}>{t('league.matchesTitle', 'Matches')}</Typography>
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {sortedMatches.map(({ match }, i) => (
            <Grid item xs={12} sm={6} md={4} key={match.id}>
              <Card>
                <CardActionArea onClick={() => navigate(`/${match.alias || match.id}`)}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{match.title || `Match ${i + 1}`}</Typography>
                    <Typography variant="caption" color="text.secondary">{fmtDate(match.startDate)}</Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h6">{t('league.standingsTitle', 'Standings')}</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>{t('league.division', 'Division')}</InputLabel>
              <Select value={division} label={t('league.division', 'Division')} onChange={(e) => setDivision(e.target.value)}>
                <MenuItem value="all">{t('league.allDivisions', 'All divisions')}</MenuItem>
                {divisions.map((d) => <MenuItem key={d} value={d}>{t(`divisions.${toDivisionKey(d)}`, { defaultValue: d })}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>{t('league.category', 'Category')}</InputLabel>
              <Select value={category} label={t('league.category', 'Category')} onChange={(e) => setCategory(e.target.value)}>
                <MenuItem value="all">{t('league.allCategories', 'All categories')}</MenuItem>
                {categories.map((c) => <MenuItem key={c} value={c}>{t(`categories.${toCategoryKey(c)}`, { defaultValue: c })}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {displayed.length === 0 ? (
          <Alert severity="info">{t('league.noStandings', 'No published scores yet — standings will appear here once matches publish their results.')}</Alert>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>{t('league.shooter', 'Shooter')}</TableCell>
                <TableCell>{t('league.division', 'Division')}</TableCell>
                <TableCell>{t('league.category', 'Category')}</TableCell>
                {sortedMatches.map(({ match }, i) => (
                  <TableCell
                    key={match.id}
                    align="right"
                    title={match.title || `Match ${i + 1}`}
                    sx={{ whiteSpace: 'normal', maxWidth: 120, fontSize: 12, verticalAlign: 'bottom' }}
                  >
                    {match.title || `Match ${i + 1}`}
                  </TableCell>
                ))}
                <TableCell align="right">{t('league.totalPoints', 'Total')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayed.map((entry, i) => (
                <TableRow key={`${entry.publicId}|${entry.division}|${entry.category}`}>
                  <TableCell>{i + 1}</TableCell>
                  <TableCell>{entry.name} <Typography component="span" variant="caption" color="text.secondary">({entry.publicId})</Typography></TableCell>
                  <TableCell>{t(`divisions.${toDivisionKey(entry.division)}`, { defaultValue: entry.division })}</TableCell>
                  <TableCell>{t(`categories.${toCategoryKey(entry.category)}`, { defaultValue: entry.category })}</TableCell>
                  {sortedMatches.map(({ match }) => {
                    const v = entry.perMatch[match.id];
                    if (v === 'DQ') return <TableCell key={match.id} align="right" sx={{ color: 'error.main' }}>DQ</TableCell>;
                    if (v == null) return <TableCell key={match.id} align="right" sx={{ color: 'text.disabled' }}>—</TableCell>;
                    return <TableCell key={match.id} align="right">{(v / 100).toFixed(2)}%</TableCell>;
                  })}
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{(entry.total / 100).toFixed(2)}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Container>
    </>
  );
}
